import type { ReachableSurface } from '@/shared/permissions/useEffectiveAuthority';
import type { Locale } from '@/shared/types/api';

import type { AnchorId } from './anchors';
import type { GuidanceOutcomeId } from './outcomes';
import type { GuidePageId, SafeRouteId } from './safeRoutes';

/**
 * The guidance contract (CONTRACT §2): what a tour IS. The engine, the help
 * panel and the catalogue build to these types.
 *
 * ── Safety rules every definition obeys (CONTRACT §4, brief §8) ─────────────
 * - A tour never changes business data: no POST/PUT/PATCH/DELETE, no mutation
 *   hook, no `element.click()` on a business control, no form submission, no
 *   draft created or edited, no budget reserved or released, no mail sent, no
 *   award, issue, cancel, acknowledge, dispatch, receipt, upload, role or MFA
 *   change.
 * - It navigates only to a `SafeRouteId`, through the router.
 * - It targets elements only through a registered `AnchorId`.
 * - Eligibility and step visibility come from the SAME permission and
 *   effective-authority checks the UI uses, with no network request. They
 *   decide what a guide explains; the server still decides what anybody may do.
 * - It never shows, reads or stores a password, secret, MFA QR/seed/code,
 *   recovery code or token.
 * - Finishing a guide means only that: a guide was finished. It is not audit,
 *   acceptance or business completion.
 */

export const PORTALS = ['tenant', 'vendor', 'platform'] as const;
export type Portal = (typeof PORTALS)[number];

export const TOUR_IDS = [
  'T01',
  'T02',
  'T03',
  'T04',
  'T05',
  'T06',
  'T07',
  'T08',
  'T09',
  'T10',
  'T11',
  'T12',
] as const;
export type TourId = (typeof TOUR_IDS)[number];

/**
 * Layer B (TASK §11): the guided workflow coaches. A coach is a MODE of the
 * same engine (`RunMode` `coach`), with its own catalogue, its own IDs and its
 * own progress entry.
 */
export const COACH_IDS = ['C01', 'C02', 'C03', 'C04', 'C05', 'C06', 'C07', 'C08'] as const;
export type CoachId = (typeof COACH_IDS)[number];

/** What the guide store runs: a tour, or a coach. */
export type GuideId = TourId | CoachId;

export function isCoachId(value: unknown): value is CoachId {
  return typeof value === 'string' && (COACH_IDS as readonly string[]).includes(value);
}

/**
 * The opaque public IDs a shell hands `GuidanceRoot`, from its own session.
 *
 * Tenant: `tenantId` + `userId`. Supplier: `supplierId` + `buyerId` (the
 * buying tenant) + `userId`. Operator: `operatorId`. They are hashed into the
 * progress key (CONTRACT §5) and never stored or shown raw — never an email,
 * a name or a business-record ID.
 */
export interface GuidanceScope {
  tenantId?: string;
  userId?: string;
  supplierId?: string;
  buyerId?: string;
  operatorId?: string;
}

/** An i18n key for guidance copy; tour copy lives under `guidance.tours.<id>.*`. */
export type GuidanceKey = `guidance.${string}`;

/**
 * What a predicate may know. Built by `GuidanceRoot` from the shell's own
 * hooks; every predicate is pure and synchronous.
 */
export interface GuidanceContext {
  portal: Portal;
  /** Opaque hash of the scope (CONTRACT §5) — never an email, name or raw record ID. */
  scopeKey: string;
  /** The same permission check the UI uses (`usePermissions().can`, or the portal's own). */
  can: (permission: string) => boolean;
  /**
   * The server-decided reach the UI uses (`useEffectiveAuthority`), which is how
   * a delegate — who holds no standing approval permission — reaches approvals.
   * Always false outside the tenant portal.
   */
  canReach: (surface: ReachableSurface) => boolean;
  pathname: string;
  /** The current route's params, for record-bound steps only. */
  params: Readonly<Record<string, string>>;
  locale: Locale;
  /** Below the 640px boundary (`PHONE_QUERY`). */
  isPhone: boolean;
  /**
   * From the 1024px boundary up: the tenant sidebar is shown, not the menu
   * button and the bottom navigation (the product's only other breakpoint).
   */
  isWide: boolean;
}

/** Anchors, and safe routes, of one portal: a tenant tour cannot name a supplier anchor. */
export type PortalAnchorId<P extends Portal> = Extract<AnchorId, `${P}.${string}`>;
export type PortalSafeRouteId<P extends Portal> = Extract<SafeRouteId, `${P}.${string}`>;
export type PortalGuidePageId<P extends Portal> = Extract<GuidePageId, `${P}.${string}`>;

/**
 * One anchor, or responsive alternatives for the same thing (the sidebar on a
 * desktop, the menu button below it): the first that resolves to exactly ONE
 * visible element is spotlit.
 */
export type StepAnchor<P extends Portal = Portal> =
  PortalAnchorId<P> | readonly [PortalAnchorId<P>, ...PortalAnchorId<P>[]];

export interface TourStep<P extends Portal = Portal> {
  /** Stable and unique within its tour, e.g. `t02-search`. Safe to store. */
  id: string;
  /** The one visible `[data-tour]` element to spotlight; `null` is a centred card. */
  anchor: StepAnchor<P> | null;
  /**
   * Navigate here, with the router, before this step. A safe route only; never
   * while a form is dirty (`isAnyGuidanceDirty`). Never a record route (one
   * with params): the engine pauses instead — the person opens the record, and
   * a `needsRecord` step explains it.
   */
  route?: PortalSafeRouteId<P>;
  /** Whether this actor sees the step. Hidden steps are not counted in "Step n of m". */
  when?: (ctx: GuidanceContext) => boolean;
  /**
   * A record-specific step: it continues only when the user is ALREADY on an
   * authorised record of this page; otherwise the tour pauses and explains
   * (`explainKey`). The engine never picks a record.
   */
  needsRecord?: { routeId: PortalGuidePageId<P>; explainKey: GuidanceKey };
  titleKey: GuidanceKey;
  bodyKey: GuidanceKey;
  /**
   * A record step whose control is absent BY DESIGN on some records (Reorder
   * on a draft request; no change available on an order). Once `present` —
   * another part of the same record — shows that the record is on screen, the
   * step explains that (`explainKey`) on a centred card the guide can finish,
   * instead of pausing on "not showing" (TOUR-04). Without that proof the guide
   * pauses as before: it never guesses.
   */
  absent?: { present: PortalAnchorId<P>; explainKey: GuidanceKey };
  /**
   * The anchor is a business commit control. The card explains it and offers
   * Finish or Pause — never an action label — and the user acts afterwards in
   * the ordinary UI. The engine never presses it.
   */
  commitNote?: boolean;
}

export interface TourDefinition<P extends Portal = Portal> {
  id: TourId;
  /** Raised when the steps change meaningfully; stored progress of another version is ignored. */
  version: number;
  portal: P;
  titleKey: GuidanceKey;
  summaryKey: GuidanceKey;
  /** Pure, synchronous, no network request. */
  eligible: (ctx: GuidanceContext) => boolean;
  /**
   * Pages where "Guide me on this page" offers this tour: safe routes, or
   * current-page-only routes (a guide there never navigates away or submits).
   */
  pageRoutes: readonly PortalGuidePageId<P>[];
  /** Typically 4–7. */
  steps: readonly TourStep<P>[];
}

/** Declares a tour, checking that its anchors and routes all belong to its portal. */
export function defineTour<P extends Portal>(definition: TourDefinition<P>): TourDefinition<P> {
  return definition;
}

/**
 * A coach step's kind (UI-6):
 * - `explain`: the modal card with the spotlight, as a tour's step — Next;
 * - `handoff`: the modal card titled "You will now perform the real action",
 *   naming the control and its consequence — "I'm ready", never an action label;
 * - `await`: the non-modal dock while the PERSON acts in the real UI; it moves
 *   on only when `until` holds;
 * - `observe`: the dock explaining another actor's step; never says the person
 *   can do it;
 * - `finish`: the last card.
 */
export type CoachStepKind = 'explain' | 'handoff' | 'await' | 'observe' | 'finish';

/**
 * When an `await` step is done. EVERY clause given must hold:
 * - `outcome`: a success of this outcome, on the bus, during this run and after
 *   the step began (with `variant` in the set, when given);
 * - `route`: the page the person is on;
 * - `probe`: the anchor's `data-guidance-state`, which pages render from server
 *   data only, is in the set.
 *
 * After a reload, Back or a new sign-in there is no bus event: a resumed step
 * is decided by its route and probe alone, and a step with neither waits for
 * the outcome again. Never a timer; never an assumed success.
 */
export interface CoachUntil<P extends Portal = Portal> {
  outcome?: GuidanceOutcomeId;
  variant?: readonly string[];
  route?: PortalGuidePageId<P>;
  probe?: { anchor: PortalAnchorId<P>; in: readonly string[] };
}

export interface CoachStep<P extends Portal = Portal> {
  /** Stable and unique within its coach, e.g. `c01-add`. Safe to store. */
  id: string;
  kind: CoachStepKind;
  /** The page this step is shown on (an `await` step also accepts its `until.route`). */
  page: PortalGuidePageId<P>;
  /**
   * Navigate here, with the router, before this step — only right after the
   * person moved (start, Next, Back), never while a form is dirty, and only to a
   * safe route without params.
   */
  route?: PortalSafeRouteId<P>;
  /** The one visible `[data-tour]` element; `null` is a centred card. */
  anchor: StepAnchor<P> | null;
  /** Whether this actor sees the step. Hidden steps are not counted. */
  when?: (ctx: GuidanceContext) => boolean;
  titleKey: GuidanceKey;
  bodyKey: GuidanceKey;
  /**
   * A `handoff`'s consequence, in the PAGE's own words where it has them (the
   * review's `request.submitExplains`) — so the coach never restates a rule.
   */
  consequenceKey?: string;
  /** An `await` step's completion rule. */
  until?: CoachUntil<P>;
  /** An `await` step whose control is disabled: why, instead of waiting in silence. */
  disabledExplainKey?: GuidanceKey;
  /** As a tour's: the control does not apply to the record that IS on screen. */
  absent?: { present: PortalAnchorId<P>; explainKey: GuidanceKey };
}

export interface CoachDefinition<P extends Portal = Portal> {
  id: CoachId;
  /** Raised when the steps change meaningfully; progress of another version is ignored. */
  version: number;
  portal: P;
  titleKey: GuidanceKey;
  summaryKey: GuidanceKey;
  /** Pure, synchronous, no network request: the same permission hooks the UI uses. */
  eligible: (ctx: GuidanceContext) => boolean;
  steps: readonly CoachStep<P>[];
}

/** Declares a coach, checking that its anchors, pages and routes all belong to its portal. */
export function defineCoach<P extends Portal>(definition: CoachDefinition<P>): CoachDefinition<P> {
  return definition;
}
